import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';
import {
  formatMoqLabel,
  getEffectiveMoq,
  isFactoryProductListing,
} from '../../util/factoryListing';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values for the card
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const isFactoryProduct = isFactoryProductListing(publicData);
  const agentName = publicData?.agentName || null;
  const authorName = isFactoryProduct
    ? agentName
      ? intl.formatMessage({ id: 'ListingCard.viaNamedAgent' }, { agentName })
      : intl.formatMessage({ id: 'ListingCard.viaSourcingAgent' })
    : intl.formatMessage({ id: 'ListingCard.author' }, { authorName: authorDisplayName });

  if (typeof window !== 'undefined' && isFactoryProduct) {
    // Debug: where ListingCard gets the sourcing agent label from
    // eslint-disable-next-line no-console
    console.log('[ListingCard] sourcing agent source', {
      listingId: listing?.id?.uuid,
      from: 'listing.attributes.publicData.agentName',
      publicData_agentName: publicData?.agentName || null,
      listingAuthor_NOT_agent: authorDisplayName,
      shownAs: authorName,
    });
  }

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const unit = publicData?.unitOfMeasurement || 'pcs';
  const moq = getEffectiveMoq(publicData);
  const moqLabel = isFactoryProduct ? formatMoqLabel(moq, unit) : null;
  const categoryLabel = isFactoryProduct ? publicData?.category || null : null;
  const industryKey = isFactoryProduct ? publicData?.industry || null : null;
  const sourcingAgentName = isFactoryProduct ? agentName : null;

  const cardAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessage }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    isFactoryProduct,
    moqLabel,
    categoryLabel,
    industryKey,
    sourcingAgentName,
  };
};
